import { IRenderLayerStats } from "../webgl_2d/core/types/webgl_types.js";
import { Vfx2DRuntime } from "../webgl_2d/vfx_2d_runtime.js";
import { WebGLUIRuntime } from "../webgl_2d/webgl_ui_runtime.js";

type RowRef = { el: HTMLDivElement; val: HTMLSpanElement; bar: HTMLDivElement | null };

export class MemoryOverlay
{
  private static container: HTMLDivElement | null                   = null;
  private static interval_id: ReturnType<typeof setInterval> | null = null;
  private static visible: boolean                                   = false;
  private static row_refs: Record<string, RowRef>                   = {};

  private static mutation_observer: MutationObserver | null         = null;
  private static last_added_el: string                              = "—";
  private static added_since_last_update: number                    = 0;
  private static last_added_el_label: HTMLSpanElement | null        = null;

  // ------------------------------------------------------------------ public

  public static show(): void
  {
    if ( this.visible )
      return;

    this.visible = true;
    this.build();
    this.startMutationObserver();
    this.update();
    this.interval_id = setInterval( () => this.update(), 1000 );
  }

  public static hide(): void
  {
    if ( !this.visible )
      return;

    this.visible = false;
    if ( this.interval_id !== null )
    {
      clearInterval( this.interval_id );
      this.interval_id = null;
    }
    this.mutation_observer?.disconnect();
    this.mutation_observer   = null;
    this.last_added_el_label = null;
    this.container?.remove();
    this.container           = null;
    this.row_refs            = {};
  }

  public static toggle(): void
  {
    this.visible ? this.hide() : this.show();
  }

  public static isVisible(): boolean
  {
    return this.visible;
  }

  // ------------------------------------------------------------------ build

  private static build(): void
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = [
      "position:absolute",
      "top:10px",
      "right:10px",
      "z-index:2147483646",
      "pointer-events:none",
      "width:230px",
      "background:rgba(0,0,0,0.85)",
      "border:1px solid rgba(255,255,255,0.15)",
      "border-radius:8px",
      "padding:8px 10px 10px",
      "font:11px/1.5 monospace",
      "color:#fff",
      "box-shadow:0 2px 14px rgba(0,0,0,0.6)",
    ].join( ";" );

    this.addTitle( wrap, "◆ MEMORY OVERLAY" );

    this.addSection( wrap, "RAM" );
    this.addRow( wrap, "heap_used",   "JS Heap used",  true );
    this.addRow( wrap, "heap_total",  "JS Heap total", false );
    this.addRow( wrap, "heap_limit",  "JS Heap limit", false );

    this.addSection( wrap, "DOM" );
    this.addRow( wrap, "dom_nodes",   "DOM nodes",     true );
    this.addRow( wrap, "canvas_cnt",  "Canvases",      false );
    this.addRow( wrap, "canvas_mem",  "Canvas buf",    true );
    this.addRow( wrap, "dom_iframes", "iframes",       false );
    this.addRow( wrap, "dom_scripts", "scripts",       false );
    this.addRow( wrap, "dom_divs",    "divs",          false );

    this.addSection( wrap, "MUTATIONS (added/sec)" );
    this.addRow( wrap, "mut_added",   "+nodes/sec",    true );

    const mut_label = document.createElement( "span" );
    mut_label.style.cssText = "color:#777;font-size:9px;word-break:break-all;display:block;margin-bottom:6px";
    mut_label.textContent   = "last: —";
    wrap.appendChild( mut_label );
    this.last_added_el_label = mut_label;

    this.addSection( wrap, "WEBGL UI ( A - Active, P - Pending )" );
    this.addRow( wrap, "webgl_ui_main",     "Main UI",       true );

    this.addSection( wrap, "GPU / WEBGL UI DYNAMIC ATLAS" );
    this.addRow( wrap, "atlas_imgs",   "Images",    true );
    this.addRow( wrap, "atlas_layers", "Layers",    true );
    this.addRow( wrap, "atlas_row",    "Row Free",  true );
    this.addRow( wrap, "atlas_col",    "Col Free",  true );
    this.addRow( wrap, "atlas_last",   "Last Size Image", false );

    this.addSection( wrap, "VFX ( A - Active, P - Pending )" );
    this.addRow( wrap, "vfx_main_back",    "Main Back",     true );
    this.addRow( wrap, "vfx_main_front",   "Main Front",    true );
    this.addRow( wrap, "vfx_overlay_back",  "Overlay Back",  true );
    this.addRow( wrap, "vfx_overlay_front", "Overlay Front", true );
    this.addRow( wrap, "vfx_total",         "Total",         true );

    document.documentElement.appendChild( wrap );
    this.container = wrap;
  }

  private static startMutationObserver(): void
  {
    this.added_since_last_update = 0;
    this.mutation_observer = new MutationObserver( ( mutations ) =>
    {
      for ( const m of mutations )
      {
        for ( const node of Array.from( m.addedNodes ) )
        {
          if ( !( node instanceof Element ) )
            continue;
          if ( node === this.container || this.container?.contains( node ) )
            continue;

          this.added_since_last_update++;
          const tag = node.tagName.toLowerCase();
          const id  = node.id ? `#${node.id}` : "";
          const cls = node.className && typeof node.className === "string"
            ? `.${node.className.trim().split( /\s+/ ).join( "." )}`
            : "";
          this.last_added_el = `${tag}${id}${cls}`;
        }
      }
    } );
    this.mutation_observer.observe( document.documentElement, { childList: true, subtree: true } );
  }

  private static addTitle( parent: HTMLElement, text: string ): void
  {
    const el         = document.createElement( "div" );
    el.textContent   = text;
    el.style.cssText = "color:#888;font-size:10px;letter-spacing:1px;margin-bottom:7px";
    parent.appendChild( el );
  }

  private static addSection( parent: HTMLElement, text: string ): void
  {
    const el         = document.createElement( "div" );
    el.textContent   = text;
    el.style.cssText = "color:#555;font-size:9px;letter-spacing:2px;text-transform:uppercase;margin:8px 0 4px";
    parent.appendChild( el );
  }

  private static addRow( parent: HTMLElement, key: string, label: string, has_bar: boolean ): void
  {
    const wrap         = document.createElement( "div" );
    wrap.style.cssText = "margin-bottom:3px";

    const lbl         = document.createElement( "span" );
    lbl.textContent   = label;
    lbl.style.cssText = "color:#666;display:inline-block;width:100px;font-size:10px";

    const val         = document.createElement( "span" );
    val.style.cssText = "font-size:11px;font-weight:bold";

    wrap.appendChild( lbl );
    wrap.appendChild( val );

    let bar: HTMLDivElement | null = null;
    if ( has_bar )
    {
      const track         = document.createElement( "div" );
      track.style.cssText = "height:2px;background:rgba(255,255,255,0.07);border-radius:2px;margin-top:2px";
      bar                 = document.createElement( "div" );
      bar.style.cssText   = "height:2px;border-radius:2px;width:0;transition:width .5s,background-color .5s";
      track.appendChild( bar );
      wrap.appendChild( track );
    }

    parent.appendChild( wrap );
    this.row_refs[ key ] = { el: wrap, val, bar };
  }

  // ------------------------------------------------------------------ update

  private static update(): void
  {
    if ( !this.container )
      return;

    const perf_mem   = ( performance as {
      memory?: {
        usedJSHeapSize?: number;
        totalJSHeapSize?: number;
        jsHeapSizeLimit?: number;
      };
    } ).memory;
    const heap_used  = perf_mem?.usedJSHeapSize  ?? -1;
    const heap_total = perf_mem?.totalJSHeapSize ?? -1;
    const heap_limit = perf_mem?.jsHeapSizeLimit ?? -1;

    const dom_nodes  = document.querySelectorAll( "*" ).length;
    const canvases   = Array.from( document.querySelectorAll( "canvas" ) ) as HTMLCanvasElement[];
    const canvas_mem = canvases.reduce( ( s, c ) => s + c.width * c.height * 4, 0 );
    const iframes    = document.querySelectorAll( "iframe" ).length;
    const scripts    = document.querySelectorAll( "script" ).length;
    const divs       = document.querySelectorAll( "div" ).length;

    const fmtMB    = ( b: number ) => ( b < 0 ? "N/A" : ( b / 1048576 ).toFixed( 1 ) + " MB" );
    const clrMem   = ( used: number, limit: number ) =>
      used < 0
        ? "#555"
        : limit > 0 && used > limit * 0.8
        ? "#f55"
        : limit > 0 && used > limit * 0.5
        ? "#fa0"
        : "#5d5";

    const clrCount = ( v: number, warn: number, danger: number ) =>
      v >= danger ? "#f55" : v >= warn ? "#fa0" : "#5d5";

    const pct      = ( v: number, max: number ) => ( max > 0 ? Math.min( 100, ( v / max ) * 100 ) : 0 );

    this.set( "heap_used",   fmtMB( heap_used ),           clrMem( heap_used, heap_limit ),     pct( heap_used, heap_limit ) );
    this.set( "heap_total",  fmtMB( heap_total ),          "#aaa" );
    this.set( "heap_limit",  fmtMB( heap_limit ),          "#aaa" );

    this.set( "dom_nodes",   String( dom_nodes ),          clrCount( dom_nodes, 1500, 3000 ),   pct( dom_nodes, 4000 ) );
    this.set( "canvas_cnt",  String( canvases.length ),    "#aaa" );
    this.set( "canvas_mem",  fmtMB( canvas_mem ),          clrCount( canvas_mem, 80e6, 150e6 ), pct( canvas_mem, 200e6 ) );
    this.set( "dom_iframes", String( iframes ),            clrCount( iframes, 3, 10 ) );
    this.set( "dom_scripts", String( scripts ),            clrCount( scripts, 20, 50 ) );
    this.set( "dom_divs",    String( divs ),               clrCount( divs, 200, 500 ) );

    const added = this.added_since_last_update;
    this.added_since_last_update = 0;
    this.set( "mut_added", `+${added}`, clrCount( added, 5, 20 ), pct( added, 30 ) );
    if ( this.last_added_el_label )
      this.last_added_el_label.textContent = `last: ${this.last_added_el}`;

    const fmt      = ( s: IRenderLayerStats ) => `${s.active} A + ${s.pending} P = ${s.active + s.pending}`;
    const getTotal = ( s: IRenderLayerStats ) => s.active + s.pending;

    const webgl_ui_stats      = WebGLUIRuntime.getStats();
    const webgl_ui_main_total = getTotal( webgl_ui_stats );
    this.set( "webgl_ui_main", fmt( webgl_ui_stats ), clrCount( webgl_ui_main_total, 1000, 2000 ), pct( webgl_ui_main_total, 2000 ) );

    const atlas = WebGLUIRuntime.getMainAtlasStats();
    const in_use = ( atlas as any ).in_use ?? 0;
    const img_label = in_use > 0 ? `${atlas.active} (${in_use} in use)` : `${atlas.active}`;
    this.set( "atlas_imgs",   img_label,   "#5d5", pct( atlas.active, 100 ) );
    this.set( "atlas_layers", `${atlas.layer}/${atlas.total}`, clrCount( atlas.layer, 5, 7 ), pct( atlas.layer, atlas.total ) );
    this.set( "atlas_row",    `${atlas.shelf_free_w} px`, "#5d5", pct( atlas.shelf_free_w, atlas.size ) );
    this.set( "atlas_col",    `${atlas.layer_free_h} px`, "#5d5", pct( atlas.layer_free_h, atlas.size ) );
    this.set( "atlas_last",   `${atlas.last_w}x${atlas.last_h}`, "#aaa" );

    const vfx_stats = Vfx2DRuntime.getStats();
    const mb_total  = getTotal( vfx_stats.main_back_layer );
    const mf_total  = getTotal( vfx_stats.main_front_layer );
    const ob_total  = getTotal( vfx_stats.overlay_back );
    const of_total  = getTotal( vfx_stats.overlay_front );

    const vfx_all_active  = vfx_stats.main_back_layer.active + vfx_stats.main_front_layer.active + vfx_stats.overlay_back.active + vfx_stats.overlay_front.active;
    const vfx_all_pending = vfx_stats.main_back_layer.pending + vfx_stats.main_front_layer.pending + vfx_stats.overlay_back.pending + vfx_stats.overlay_front.pending;
    const vfx_all_total   = vfx_all_active + vfx_all_pending;

    this.set( "vfx_main_back",    fmt( vfx_stats.main_back_layer ),  clrCount( mb_total, 500, 1000 ),   pct( mb_total, 1000 ) );
    this.set( "vfx_main_front",   fmt( vfx_stats.main_front_layer ), clrCount( mf_total, 500, 1000 ),   pct( mf_total, 1000 ) );
    this.set( "vfx_overlay_back",  fmt( vfx_stats.overlay_back ),     clrCount( ob_total, 1000, 2000 ), pct( ob_total, 2000 ) );
    this.set( "vfx_overlay_front", fmt( vfx_stats.overlay_front ),    clrCount( of_total, 1000, 2000 ), pct( of_total, 2000 ) );
    this.set( "vfx_total",         `${vfx_all_active} A + ${vfx_all_pending} P = ${vfx_all_total}`, clrCount( vfx_all_total, 3000, 6000 ), pct( vfx_all_total, 6000 ) );
  }

  private static set( key: string, text: string, color: string, bar_pct?: number ): void
  {
    const ref = this.row_refs[ key ];
    if ( !ref )
      return;

    ref.val.textContent = text;
    ref.val.style.color = color;
    if ( ref.bar && bar_pct !== undefined )
    {
      ref.bar.style.width           = bar_pct.toFixed( 1 ) + "%";
      ref.bar.style.backgroundColor = color;
    }
  }
}
